import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Trash2, Share2, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { setCompareItems } from '@/lib/compareStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/** Saved comparisons (paid) — reopen, re-share or drop a shortlist. */
const MyComparisons = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/compare/saved');
      setRows(data || []);
    } catch {
      toast.error('Could not load your saved comparisons');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/compare/saved/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success('Comparison deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const reopen = async (row) => {
    // Load the saved shortlist back into the working tray so it can be
    // edited (drop one, add another) and re-sent as an RFQ.
    try {
      const { data } = await api.get('/compare/listings', { params: { ids: (row.listing_ids || []).join(',') } });
      setCompareItems((data.items || [])
        .filter((i) => i.availability_state !== 'removed')
        .map((i) => ({
          id: i.id,
          title: i.title,
          listing_type: i.listing_type,
          thumb_url: (i.media || []).find((m) => (m.kind || 'image') === 'image')?.thumb_url || null,
        })));
      window.location.href = '/compare';
    } catch {
      toast.error('Could not reopen that comparison');
    }
  };

  const copyShare = (row) => {
    const url = `${window.location.origin}/compare/s/${row.share_token}`;
    navigator.clipboard?.writeText(url);
    setCopied(row.id);
    toast.success('Share link copied');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className='space-y-5' data-testid='my-comparisons-page'>
      <div>
        <h1 className='flex items-center gap-2 text-xl font-bold tracking-tight'>
          <Scale className='h-5 w-5 text-primary' /> Saved comparisons
        </h1>
        <p className='mt-1 text-sm text-muted-foreground'>
          Named shortlists you kept. Open one to edit it or re-send the quote request.
        </p>
      </div>

      {loading ? (
        <div className='py-10 text-center text-muted-foreground'>Loading…</div>
      ) : rows.length === 0 ? (
        <Card className='p-8 text-center' data-testid='my-comparisons-empty'>
          <Scale className='mx-auto h-8 w-8 text-muted-foreground' />
          <div className='mt-2 font-semibold'>No saved comparisons yet</div>
          <p className='mx-auto mt-1 max-w-sm text-sm text-muted-foreground'>
            Shortlist listings while browsing, then save the comparison from the compare page.
          </p>
          <Button asChild size='sm' className='mt-4'><Link to='/sell'>Browse listings</Link></Button>
        </Card>
      ) : (
        <div className='space-y-3'>
          {rows.map((row) => (
            <Card key={row.id} className='flex flex-wrap items-center justify-between gap-3 p-4' data-testid={`saved-comparison-${row.id}`}>
              <div className='min-w-0'>
                <div className='truncate font-semibold'>{row.name}</div>
                <div className='text-xs text-muted-foreground'>
                  {(row.listing_ids || []).length} listing{(row.listing_ids || []).length > 1 ? 's' : ''}
                  {row.listing_type ? ` · ${row.listing_type}` : ''}
                  {row.updated_at ? ` · updated ${new Date(row.updated_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <Button size='sm' variant='outline' onClick={() => copyShare(row)} data-testid={`saved-comparison-share-${row.id}`}>
                  {copied === row.id ? <Check className='mr-1.5 h-4 w-4' /> : <Share2 className='mr-1.5 h-4 w-4' />}
                  {copied === row.id ? 'Copied' : 'Share link'}
                </Button>
                <Button size='sm' onClick={() => reopen(row)} data-testid={`saved-comparison-open-${row.id}`}>
                  <ExternalLink className='mr-1.5 h-4 w-4' /> Open
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  className='text-destructive hover:text-destructive'
                  onClick={() => remove(row.id)}
                  data-testid={`saved-comparison-delete-${row.id}`}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyComparisons;
